import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import OrgPickerList from './OrgPickerList';
import { bulkOrgAction } from './api';
import type { BulkRowResult, OrgsMeta } from './types';
import { fieldClass, labelClass, primaryBtnClass } from '../styles';

interface BulkEditOrganizationsProps {
  meta: OrgsMeta;
}

type BulkAction =
  | 'set_tier'
  | 'set_currency'
  | 'set_account_manager'
  | 'set_org_admin'
  | 'deactivate'
  | 'activate';

const BulkEditOrganizations: React.FC<BulkEditOrganizationsProps> = ({ meta }) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [listKey, setListKey] = useState(0);
  const [action, setAction] = useState<BulkAction>('set_tier');
  const [tierId, setTierId] = useState<number | ''>('');
  const [currencyId, setCurrencyId] = useState<number | ''>('');
  const [accountManagerId, setAccountManagerId] = useState<number | ''>('');
  const [orgAdminId, setOrgAdminId] = useState<number | ''>('');
  const [orgAdminEmail, setOrgAdminEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [results, setResults] = useState<BulkRowResult[] | null>(null);

  const actionDisabled = useMemo(() => selectedIds.length === 0, [selectedIds.length]);

  const apply = async () => {
    if (selectedIds.length === 0) {
      setError('Select at least one organization.');
      return;
    }
    if (action === 'deactivate' && !window.confirm(`Deactivate ${selectedIds.length} organization(s)?`)) {
      return;
    }
    const payload: Record<string, unknown> = {
      org_ids: selectedIds,
      action,
    };
    if (action === 'set_tier') {
      if (!tierId) {
        setError('Choose a tier.');
        return;
      }
      payload.tier_id = tierId;
    }
    if (action === 'set_currency') {
      if (!currencyId) {
        setError('Choose a currency.');
        return;
      }
      payload.currency_id = currencyId;
    }
    if (action === 'set_account_manager') {
      if (!accountManagerId) {
        setError('Choose an account manager.');
        return;
      }
      payload.account_manager_id = accountManagerId;
    }
    if (action === 'set_org_admin') {
      if (orgAdminId) payload.org_admin_id = orgAdminId;
      else if (orgAdminEmail.trim()) payload.org_admin_email = orgAdminEmail.trim();
      else {
        setError('Choose or enter an organization admin.');
        return;
      }
    }
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const payloadRes = await bulkOrgAction(payload);
      setResults(payloadRes.results);
      setSummary(
        `Updated ${payloadRes.updated}` +
          (payloadRes.errors ? `, ${payloadRes.errors} could not be updated.` : '.')
      );
      setListKey((n) => n + 1);
      setSelectedIds([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <OrgPickerList
        meta={meta}
        mode="multi"
        selectedIds={selectedIds}
        refreshToken={listKey}
        onChange={(ids) => setSelectedIds(ids)}
      />
      <div className="max-w-3xl border-t border-[#E8E8E8] pt-5">
        <h3 className="text-base font-semibold text-[#0C0B0B] mb-3">Bulk action</h3>
        {error && (
          <div className="mb-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
        )}
        {summary && (
          <div className="mb-3 rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
            {summary}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as BulkAction)}
              className={fieldClass}
            >
              <option value="set_tier">Change tier</option>
              <option value="set_currency">Change currency</option>
              <option value="set_account_manager">Change account manager</option>
              <option value="set_org_admin">Change organization admin</option>
              <option value="deactivate">Deactivate</option>
              <option value="activate">Reactivate</option>
            </select>
          </div>
          {action === 'set_tier' && (
            <div>
              <label className={labelClass}>Tier</label>
              <select
                value={tierId}
                onChange={(e) => setTierId(e.target.value ? Number(e.target.value) : '')}
                className={fieldClass}
              >
                <option value="">Select a tier</option>
                {meta.tiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {action === 'set_currency' && (
            <div>
              <label className={labelClass}>Currency</label>
              <select
                value={currencyId}
                onChange={(e) => setCurrencyId(e.target.value ? Number(e.target.value) : '')}
                className={fieldClass}
              >
                <option value="">Select a currency</option>
                {meta.currencies.map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.code}
                  </option>
                ))}
              </select>
            </div>
          )}
          {action === 'set_account_manager' && (
            <div>
              <label className={labelClass}>Account manager</label>
              <select
                value={accountManagerId}
                onChange={(e) =>
                  setAccountManagerId(e.target.value ? Number(e.target.value) : '')
                }
                className={fieldClass}
              >
                <option value="">Select an account manager</option>
                {meta.account_managers.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name || person.email}
                  </option>
                ))}
              </select>
            </div>
          )}
          {action === 'set_org_admin' && (
            <div>
              <label className={labelClass}>Organization admin</label>
              <select
                value={orgAdminId}
                onChange={(e) => {
                  setOrgAdminId(e.target.value ? Number(e.target.value) : '');
                  if (e.target.value) setOrgAdminEmail('');
                }}
                className={fieldClass}
              >
                <option value="">Or enter an email below</option>
                {meta.account_managers.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name || person.email}
                  </option>
                ))}
              </select>
              <input
                value={orgAdminEmail}
                onChange={(e) => {
                  setOrgAdminEmail(e.target.value);
                  setOrgAdminId('');
                }}
                placeholder="Or type an email"
                className={`${fieldClass} mt-2`}
              />
            </div>
          )}
        </div>
        <div className="mt-4">
          <button
            type="button"
            disabled={busy || actionDisabled}
            onClick={() => void apply()}
            className={primaryBtnClass}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
            Apply to {selectedIds.length || 0} organization{selectedIds.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
      {results && (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-left text-[12px] text-[#626262]">
              <tr>
                <th className="px-3 py-2.5 font-semibold">Name</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">Message</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <tr key={`${row.id}-${row.name}`} className="border-t border-[#E8E8E8]">
                  <td className="px-3 py-2">{row.name || '—'}</td>
                  <td className="px-3 py-2 capitalize">{row.status}</td>
                  <td className={`px-3 py-2 ${row.status === 'error' ? 'text-red-700' : ''}`}>
                    {row.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BulkEditOrganizations;
